import { SendOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  message,
  Modal,
  TimePicker,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { updateCycle, updateCycleStatus } from 'services/api/cycles';
import styles from 'styles/components/sendCycleModal.module.scss';
import { CYCLE_STATUS } from 'utils/constants';
import WarningMissingParticipants from './warningMissingParticipants';

const SendCycleModal = ({ cycle, open, onClose, onFinish }) => {
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleForm] = Form.useForm();
  const [scheduleFormValues, setScheduleFormValues] = useState({
    scheduledAt: dayjs().set('second', 0).set('minute', 0),
    scheduleTime: dayjs().set('second', 0).set('minute', 0),
  });
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({
    deadline: cycle?.deadline ? dayjs(cycle?.deadline) : null,
  });
  const [sendLoading, setSendLoading] = useState(false);

  const onValuesChange = (changedFields, allFields) => {
    setFormValues(allFields);
  };

  const onValuesChangeSchedule = (changedFields, allFields) => {
    setScheduleFormValues(allFields);
  };

  const schedule = () => {
    form
      .validateFields()
      .then(() => {
        setScheduleMode(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const send = (scheduleDate, scheduleTime) => {
    setSendLoading(true);
    let scheduledAt = scheduleDate || dayjs();
    if (scheduleTime) {
      scheduledAt = scheduledAt
        .set('hour', scheduleTime.hour())
        .set('minute', 0);
    }

    const dd = formValues.deadline || dayjs(cycle.deadline);
    const hh = formValues?.deadlineTime?.hour() || dayjs(cycle.deadline).hour();
    const deadline = dd.set('hour', hh).set('minute', '00');
    updateCycle({
      cycleId: cycle._id,
      scheduledAt: scheduledAt.format(),
      deadline: deadline.format(),
    })
      .then(() => {
        return updateCycleStatus({
          cycleId: cycle._id,
          status: scheduleDate ? CYCLE_STATUS.SCHEDULED : CYCLE_STATUS.LIVE,
        })
          .then(() => {
            onFinish();
            form.resetFields();
            scheduleForm.resetFields();
            message.success(
              scheduleDate
                ? 'Cycle scheduled successfully'
                : 'Cycle sent successfully'
            );
          })
          .catch((e) => {
            console.error(e);
            if (e.response.data.error.message === 'PAYMENT_REQUIRED') {
              message.error(
                <div>
                  Please <b>upgrade</b> to a paid plan to schedule cycles
                </div>
              );
            } else {
              message.error('Failed to send cycle, please try again');
            }
          })
          .finally(() => {
            setSendLoading(false);
          });
      })
      .catch((e) => {
        console.error(e);
        message.error('Failed to udpate cycle, please try again');
      })
      .finally(() => {
        setSendLoading(false);
      });
  };

  if (!open) return null;

  return (
    <Modal open={open} onCancel={onClose} footer={null}>
      <div>
        <h1>Send Cycle</h1>
        <WarningMissingParticipants cycle={cycle} />
        {scheduleMode ? (
          <div>
            <div>
              <div>Schedule the cycle to be sent on:</div>
              <div>
                <Form
                  form={scheduleForm}
                  onValuesChange={onValuesChangeSchedule}
                  initialValues={{
                    scheduledAt: dayjs().set('second', 0).set('minute', 0),
                    scheduleTime: dayjs().set('second', 0).set('minute', 0),
                  }}
                  onFinish={() => {
                    send(
                      scheduleFormValues.scheduledAt,
                      scheduleFormValues.scheduleTime
                    );
                  }}
                >
                  <Form.Item name="scheduledAt">
                    <DatePicker style={{ width: '200px' }} />
                  </Form.Item>
                  <Form.Item
                    label="Schedule time"
                    name="scheduleTime"
                    rules={[{ required: true, message: 'A time is required' }]}
                  >
                    <TimePicker
                      format="HH"
                      allowClear={false}
                      showNow={false}
                      needConfirm={false}
                      suffixIcon={':00'}
                      use12Hours={true}
                    />
                  </Form.Item>
                  <div className={styles.footer}>
                    <div
                      style={{
                        flexGrow: 1,
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Button onClick={onClose}>Cancel</Button>
                    </div>
                    <>
                      <Button
                        type="link"
                        onClick={() => setScheduleMode(false)}
                      >
                        Back
                      </Button>
                      <Button
                        htmlType="submit"
                        icon={<SendOutlined />}
                        type="primary"
                        style={{ width: '170px' }}
                        loading={sendLoading}
                      >
                        Schedule
                      </Button>
                    </>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Form
              layout="vertical"
              initialValues={{
                deadline: cycle?.deadline ? dayjs(cycle?.deadline) : null,
                deadlineTime: cycle?.deadline ? dayjs(cycle?.deadline) : null,
              }}
              form={form}
              onFinish={() => {
                send();
              }}
              onValuesChange={onValuesChange}
            >
              <Form.Item
                label="Deadline date"
                name="deadline"
                rules={[{ required: true, message: 'Deadline is required' }]}
              >
                <DatePicker
                  style={{ width: '200px' }}
                  disabledDate={(current) => current && current < dayjs()}
                />
              </Form.Item>
              <Form.Item
                label="Deadline time"
                name="deadlineTime"
                rules={[{ required: true, message: 'A time is required' }]}
              >
                <TimePicker
                  format="HH"
                  allowClear={false}
                  showNow={false}
                  needConfirm={false}
                  suffixIcon={':00'}
                  use12Hours={true}
                />
              </Form.Item>
              {/* <div>
                <h3>
                  Preview of the notification that will be sent to participants:
                </h3>
                <div className={styles.previewWrapper}>
                  <div className={styles.previewHeader}>
                    <Avatar size={30} src="/img/logo.png" />
                    <span>SimplePerf</span>
                  </div>
                  <div className={styles.previewContent}>
                    A new performance review cycle called {cycle?.name} just
                    kicked off, and you've got 1 task waiting for you:
                    <div>- Self Review</div>
                    <br />
                    <div>Hit the button below to get started 🚀</div>
                    <div>
                      <Button size="small" type="primary">
                        Start Review
                      </Button>
                    </div>
                  </div>
                </div>
              </div> */}
              <div>
                <Alert
                  showIcon
                  icon={<div style={{ marginTop: 14 }}>🔔</div>}
                  type="info"
                  description="Once you click on Send, a notification will be sent to participants with the deadline and the tasks."
                />
                {dayjs(cycle?.scheduledAt).isBefore(dayjs()) && (
                  <Alert
                    style={{ marginTop: 14 }}
                    showIcon
                    type="warning"
                    description="Note that people already notified in previous attempts will not receive a new notification."
                  />
                )}
              </div>
              <div className={styles.footer}>
                <div
                  style={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Button onClick={onClose}>Cancel</Button>
                </div>
                <>
                  <Button type="link" onClick={schedule}>
                    Schedule
                  </Button>
                  <Button
                    icon={<SendOutlined />}
                    type="primary"
                    style={{ width: '170px' }}
                    loading={sendLoading}
                    htmlType="submit"
                  >
                    Send now
                  </Button>
                </>
              </div>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SendCycleModal;
